'use client';

/**
 * TotalConnectFooter - Unified navigation footer for web/desktop modals
 * Shows tabs: Calls, Email, Dashboard, Messages, Calendar
 * Calls/Email/Messages only shown if user has total-connect module access
 * Calendar and Dashboard are always shown
 * 
 * NOTE: On Capacitor mobile, CapacitorFooter handles navigation instead.
 * This component is hidden on Capacitor when CapacitorFooter is in connect mode.
 */

import React from 'react';
import { Mail, MessageSquare, Phone, Calendar, LayoutDashboard, CheckSquare } from 'lucide-react';
import { isCapacitor } from '@/lib/platformUtils';
import { useTheme } from '@/contexts/ThemeContext';

export type TotalConnectTab = 'email' | 'messages' | 'calls' | 'calendar' | 'dashboard' | 'tasks';

interface TotalConnectFooterProps {
  activeTab: TotalConnectTab;
  onTabChange: (tab: TotalConnectTab) => void;
  // Optional badge counts
  emailBadge?: number;
  messagesBadge?: number;
  callsBadge?: number;
  calendarBadge?: number;
  tasksBadge?: number;
  // Whether user has total-connect module access (shows calls, email, messages)
  hasTotalConnect?: boolean;
  // Whether to include bottom safe area padding (for full-screen modals like OperationsCallModal)
  includeSafeArea?: boolean;
  // Force hide on Capacitor (CapacitorFooter handles navigation instead)
  hideOnCapacitor?: boolean;
}

export default function TotalConnectFooter({
  activeTab,
  onTabChange,
  emailBadge = 0,
  messagesBadge = 0,
  callsBadge = 0,
  calendarBadge = 0,
  tasksBadge = 0,
  hasTotalConnect = true,
  includeSafeArea = false,
  hideOnCapacitor = true,
}: TotalConnectFooterProps) {
  const { currentTheme, clientBranding } = useTheme();
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';
  const isDark = currentTheme?.mode === 'dark';

  // Hide on Capacitor — CapacitorFooter handles navigation in connect mode
  if (hideOnCapacitor && isCapacitor()) {
    return null;
  }

  // Non-Capacitor: also hide (this footer is being replaced by CapacitorFooter)
  if (!isCapacitor()) {
    return null;
  }

  // Build tabs: Calls, Email, Dashboard, Tasks, Messages, Calendar
  const allTabs: Array<{
    id: TotalConnectTab;
    icon: typeof Mail;
    label: string;
    badge: number;
    requiresTotalConnect: boolean;
  }> = [
    { id: 'calls', icon: Phone, label: 'Calls', badge: callsBadge, requiresTotalConnect: true },
    { id: 'email', icon: Mail, label: 'Email', badge: emailBadge, requiresTotalConnect: true },
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', badge: 0, requiresTotalConnect: false },
    { id: 'tasks', icon: CheckSquare, label: 'Tasks', badge: tasksBadge, requiresTotalConnect: false },
    { id: 'messages', icon: MessageSquare, label: 'Messages', badge: messagesBadge, requiresTotalConnect: true },
    { id: 'calendar', icon: Calendar, label: 'Calendar', badge: calendarBadge, requiresTotalConnect: false },
  ];

  // Filter tabs based on module access
  const tabs = allTabs.filter(tab => !tab.requiresTotalConnect || hasTotalConnect);

  return (
    <div 
      className={`flex items-center justify-around border-t ${
        isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      }`}
      style={includeSafeArea ? { paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : undefined}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex-1 flex flex-col items-center gap-0 py-1.5 px-1 transition-colors relative ${
              isActive 
                ? '' 
                : isDark 
                  ? 'text-gray-500 active:bg-gray-800' 
                  : 'text-gray-500 active:bg-gray-100'
            }`}
            style={isActive ? { color: primaryColor } : undefined}
          >
            <div className="relative">
              <Icon className="w-5 h-5" />
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-medium rounded-full">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </div>
            <span className="text-[9px] font-medium leading-tight">{tab.label}</span>
            {isActive && (
              <div 
                className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full"
                style={{ backgroundColor: primaryColor }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
